import type {
  ContractDuplicateCheckParams,
  ContractDuplicateCheckResponse,
} from './types';

import { ENDPOINT } from '~/constants/endpoints';

import { type KyRequestOptions } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import {
  zodContractDuplicateCheckParamsSchema,
  zodContractDuplicateCheckResponseSchema,
} from './validations';

/**
 * Checks whether a contract with similar attributes (contract number, supplier, and/or date
 * range) already exists, to warn the user before creating a likely-duplicate contract.
 *
 * @param params - The attributes to check for existing likely-duplicate contracts. All fields
 *   are optional; provide at least one to get a meaningful result.
 * @param options - Additional request options.
 * @returns The list of existing contracts that are likely duplicates.
 * @throws VestigasHTTPError if the API request fails.
 * @see https://app.dev.vestigas.com/redoc#tag/Contract-Import-Jobs/operation/duplicate_check_contracts_duplicate_check_post
 */
export const fetchContractDuplicateCheck = async (
  params: ContractDuplicateCheckParams,
  options?: KyRequestOptions,
) => {
  return vestigasApi
    .get(ENDPOINT.CONTRACT.DUPLICATE_CHECK(), {
      searchParams: params,
      ...options,
      validate: {
        ...options?.validate,
        searchParams: zodContractDuplicateCheckParamsSchema,
        response: zodContractDuplicateCheckResponseSchema,
      },
    })
    .json<ContractDuplicateCheckResponse>();
};
